import { Actions, ActionTypes } from '@appStore/account/actions';
import { UserSettings, Widget, MeteringFilter } from '@model';
import { MeteringFilterType, MeteringType } from '@enums';

export interface State {
  loading: boolean;
  loaded: boolean;
  data: UserSettings;
  hotWidgets: Widget[];
  coldWidgets: Widget[];
}

export const initialState: State = {
  loading: false,
  loaded: false,
  data: null,
  hotWidgets: [],
  coldWidgets: []
};

export function reducer(state = initialState, action: Actions): State {
  let hotWidgets: Widget[] = [];
  let coldWidgets: Widget[] = [];
  let widgetNames: string[] = [];

  switch (action.type) {
    case ActionTypes.SettingLoad:
      return { ...state, loaded: false, loading: true };

    case ActionTypes.SettingLoadSuccess:
      coldWidgets = action.payload.coldWidgetSettings.widgets
        ? action.payload.coldWidgetSettings.widgets.map(x => ({
            ...x,
            loaded: false,
            loading: false,
            hasError: false,
            needRefresh: false
          }))
        : [];
      hotWidgets = action.payload.hotWidgetSettings.widgets
        ? action.payload.hotWidgetSettings.widgets.map(x => ({
            ...x,
            loaded: false,
            loading: false,
            hasError: false,
            needRefresh: false
          }))
        : [];

      return {
        ...state,
        loaded: true,
        loading: false,
        data: {
          ...state.data,
          meteringpointTableview: action.payload.meteringpointTableview,
          contractTableView: action.payload.contractTableView,
          coldMeteringFilter: action.payload.coldMeteringFilter.filterType === MeteringFilterType.VirtualFilter ?
            MeteringFilter.initalState(MeteringType.Cold) :
            action.payload.coldMeteringFilter,
          coldLogicalMeteringFilter: MeteringFilter.initalState(MeteringType.ColdLogical),
          coldWidgetSettings: { ...action.payload.coldWidgetSettings, widgets: coldWidgets },
          defaultMeteringFilterAdresses: action.payload.defaultMeteringFilterAdresses,
          hotMeteringFilter: action.payload.hotMeteringFilter.filterType === MeteringFilterType.VirtualFilter ?
            MeteringFilter.initalState(MeteringType.Hot) :
            action.payload.hotMeteringFilter,
          hotLogicalMeteringFilter: MeteringFilter.initalState(MeteringType.HotLogical),
          hotWidgetSettings: { ...action.payload.hotWidgetSettings, widgets: hotWidgets },
          availableWidgets:
            state.data !== null && state.data.availableWidgets !== null ? state.data.availableWidgets : [],
          analyzeFilterView: action.payload.analyzeFilterView,
          invoiceTableView: action.payload.invoiceTableView,
          alarmTableView: action.payload.alarmTableView,
          serviceAgreementTableView: action.payload.serviceAgreementTableView
        },
        hotWidgets: hotWidgets,
        coldWidgets: coldWidgets
      };

    case ActionTypes.SettingLoadFail:
      return { ...state, loaded: false, loading: false };

    case ActionTypes.SettingUpdateSuccess:
      return {
        ...state,
        data: {
          ...state.data,
          coldMeteringFilter: action.payload.userSettings.coldMeteringFilter,
          coldLogicalMeteringFilter: action.payload.userSettings.coldLogicalMeteringFilter,
          coldWidgetSettings: action.payload.userSettings.coldWidgetSettings,
          defaultMeteringFilterAdresses: action.payload.userSettings.defaultMeteringFilterAdresses,
          hotMeteringFilter: action.payload.userSettings.hotMeteringFilter,
          hotLogicalMeteringFilter: action.payload.userSettings.hotLogicalMeteringFilter,
          hotWidgetSettings: action.payload.userSettings.hotWidgetSettings,
          availableWidgets:
            action.payload.userSettings.availableWidgets === null ? [] : action.payload.userSettings.availableWidgets,
          meteringpointTableview: action.payload.userSettings.meteringpointTableview,
          contractTableView: action.payload.userSettings.contractTableView,
          analyzeFilterView: action.payload.userSettings.analyzeFilterView,
          invoiceTableView: action.payload.userSettings.invoiceTableView,
          alarmTableView: action.payload.userSettings.alarmTableView,
          serviceAgreementTableView: action.payload.userSettings.serviceAgreementTableView
        }
      };

    case ActionTypes.FilterSettingReset:
      if (action.payload === MeteringType.Hot) {
        return {
          ...state,
          data: {
            ...state.data,
            hotMeteringFilter: {
              ...MeteringFilter.initalState(action.payload),
              filterId: 0,
              subfilter: null,
              containsMeteringPointIds: null
            }
          }
        };
      }
      if (action.payload === MeteringType.Cold) {
        return {
          ...state,
          data: {
            ...state.data,
            coldMeteringFilter: {
              ...MeteringFilter.initalState(action.payload),
              filterId: 0,
              subfilter: null,
              containsMeteringPointIds: null
            }
          }
        };
      }
      return state;

    case ActionTypes.WidgetSettingUpdateSuccess:
      if (action.payload.meteringType === MeteringType.Hot) {
        hotWidgets = action.payload.widgets
          ? action.payload.widgets.map(x => ({
              ...x,
              loaded: false,
              loading: false,
              hasError: false,
              needRefresh: false
            }))
          : [];
        return {
          ...state,
          data: {
            ...state.data,
            hotWidgetSettings: {
              ...action.payload,
              isEdit: false
            }
          },
          hotWidgets: hotWidgets
        };
      }

      if (action.payload.meteringType === MeteringType.Cold) {
        coldWidgets = action.payload.widgets
          ? action.payload.widgets.map(x => ({
              ...x,
              loaded: false,
              loading: false,
              hasError: false,
              needRefresh: false
            }))
          : [];
        return {
          ...state,
          data: {
            ...state.data,
            coldWidgetSettings: {
              ...action.payload,
              isEdit: false
            }
          },
          coldWidgets: coldWidgets
        };
      }
      return state;

    case ActionTypes.WidgetsSetAsLoading:
      if (action.payload.meteringType === MeteringType.Hot) {
        let updatedWidgets = state.hotWidgets.map(widget => {
          if (!!action.payload.widgetNames.find(x => x === widget.name)) {
            return { ...widget, loading: true, loaded: false, hasError: false };
          }
          return widget;
        });

        return {
          ...state,
          hotWidgets: updatedWidgets
        };
      }
      if (action.payload.meteringType === MeteringType.Cold) {
        let updatedWidgets = state.coldWidgets.map(widget => {
          if (!!action.payload.widgetNames.find(x => x === widget.name)) {
            return { ...widget, loading: true, loaded: false, hasError: false };
          }
          return widget;
        });
        return {
          ...state,
          coldWidgets: updatedWidgets
        };
      }
      return state;
    case ActionTypes.WidgetDataLoad:
      widgetNames = action.payload.widgetNames;
      if (action.payload.meteringType === MeteringType.Hot) {
        hotWidgets = [...state.hotWidgets];
        let updated = hotWidgets.map(widget => {
          let updatedWidget = widgetNames.filter(name => name === widget.name);
          if (updatedWidget.length > 0) {
            return <Widget>{
              ...widget,
              loaded: false,
              loading: true,
              needRefresh: false,
              hasError: false
            };
          } else {
            return widget;
          }
        });
        return {
          ...state,
          hotWidgets: updated
        };
      }
      if (action.payload.meteringType === MeteringType.Cold) {
        coldWidgets = [...state.coldWidgets];
        let updated = coldWidgets.map(widget => {
          let updatedWidget = widgetNames.filter(name => name === widget.name);
          if (updatedWidget.length > 0) {
            return <Widget>{
              ...widget,
              loaded: false,
              loading: true,
              needRefresh: false,
              hasError: false
            };
          } else {
            return widget;
          }
        });
        return {
          ...state,
          coldWidgets: updated
        };
      }
      return state;

    case ActionTypes.WidgetDataLoadFail:
      widgetNames = action.payload.widgetLoadConfig.widgetNames;
      if (action.payload.widgetLoadConfig.meteringType === MeteringType.Hot) {
        hotWidgets = [...state.hotWidgets];
        let updated = hotWidgets.map(widget => {
          let updatedWidget = widgetNames.filter(name => name === widget.name);
          if (updatedWidget.length > 0) {
            return <Widget>{
              ...widget,
              data: null,
              data2: null,
              meteringPointIds: [],
              loaded: false,
              loading: false,
              needRefresh: false,
              hasError: true
            };
          } else {
            return widget;
          }
        });
        return {
          ...state,
          hotWidgets: updated
        };
      }
      if (action.payload.widgetLoadConfig.meteringType === MeteringType.Cold) {
        coldWidgets = [...state.coldWidgets];
        let updated = coldWidgets.map(widget => {
          let updatedWidget = widgetNames.filter(name => name === widget.name);
          if (updatedWidget.length > 0) {
            return <Widget>{
              ...widget,
              data: null,
              data2: null,
              meteringPointIds: [],
              loaded: false,
              loading: false,
              needRefresh: false,
              hasError: true
            };
          } else {
            return widget;
          }
        });
        return {
          ...state,
          coldWidgets: updated
        };
      }
      return state;

    case ActionTypes.WidgetDataLoadSuccess:
      if (action.payload.meteringType === MeteringType.Hot) {
        hotWidgets = [...state.hotWidgets];
        let updated = hotWidgets.map(widget => {
          let updatedWidget = action.payload.data.filter(w => !!w && w.name === widget.name);
          let allMpIds: number[] = [];
          if (updatedWidget.length > 0) {
            if (updatedWidget[0] && updatedWidget[0].data) {
              allMpIds = [
                ...updatedWidget[0].data.decreasedMeteringPoints,
                ...updatedWidget[0].data.increasedMeteringPoints,
                ...updatedWidget[0].data.unChangedMeteringPoints
              ].map(x => x.id);
            }
            return {
              ...updatedWidget[0],
              batchLoadData: widget.batchLoadData,
              meteringPointIds: allMpIds,
              userRadioChoice: widget.userRadioChoice,
              loaded: true,
              loading: false,
              needRefresh: true,
              hasError: false
            };
          } else {
            return widget;
          }
        });
        return {
          ...state,
          hotWidgets: updated
        };
      }

      if (action.payload.meteringType === MeteringType.Cold) {
        coldWidgets = [...state.coldWidgets];
        let updated = coldWidgets.map(widget => {
          let updatedWidget = action.payload.data.filter(w => !!w && w.name === widget.name);
          let allMpIds: number[] = [];
          if (updatedWidget.length > 0) {
            if (updatedWidget[0] && updatedWidget[0].data) {
              allMpIds = [
                ...updatedWidget[0].data.decreasedMeteringPoints,
                ...updatedWidget[0].data.increasedMeteringPoints,
                ...updatedWidget[0].data.unChangedMeteringPoints
              ].map(x => x.id);
            }
            return {
              ...updatedWidget[0],
              batchLoadData: widget.batchLoadData,
              meteringPointIds: allMpIds,
              userRadioChoice: widget.userRadioChoice,
              loaded: true,
              loading: false,
              needRefresh: true,
              hasError: false
            };
          } else {
            return widget;
          }
        });
        return {
          ...state,
          coldWidgets: updated
        };
      }
      return state;

    case ActionTypes.AvailableWidgetsLoadSuccess:
      return {
        ...state,
        data: {
          ...state.data,
          availableWidgets: action.payload
        }
      };

    case ActionTypes.AvailableWidgetsLoad:
      return { ...state };

    case ActionTypes.SetSubFilter:
      return {
        ...state,
        data: {
          ...state.data,
          hotMeteringFilter: {
            ...state.data.hotMeteringFilter,
            subfilter:
              action.payload && action.payload.meteringType === MeteringType.Hot
                ? action.payload
                : state.data.hotMeteringFilter.subfilter
          },
          hotLogicalMeteringFilter: {
            ...state.data.hotLogicalMeteringFilter,
            subfilter:
              action.payload && action.payload.meteringType === MeteringType.HotLogical
                ? action.payload
                : state.data.hotLogicalMeteringFilter.subfilter
          },
          coldMeteringFilter: {
            ...state.data.coldMeteringFilter,
            subfilter:
              action.payload && action.payload.meteringType === MeteringType.Cold
                ? action.payload
                : state.data.coldMeteringFilter.subfilter
          },
          coldLogicalMeteringFilter: {
            ...state.data.coldLogicalMeteringFilter,
            subfilter:
              action.payload && action.payload.meteringType === MeteringType.ColdLogical
                ? action.payload
                : state.data.coldLogicalMeteringFilter.subfilter
          }
        }
      };

    case ActionTypes.SubFilterReset:
        if (action.payload === MeteringType.Hot) {
          return {
            ...state,
            data: {
              ...state.data,
              hotMeteringFilter: {
                ...state.data.hotMeteringFilter,
                subfilter: null
              }
            }
          };
        }
        if (action.payload === MeteringType.Cold) {
          return {
            ...state,
            data: {
              ...state.data,
              coldMeteringFilter: {
                ...state.data.coldMeteringFilter,
                subfilter: null
              }
            }
          };
        }
        if (action.payload === MeteringType.HotLogical) {
          return {
            ...state,
            data: {
              ...state.data,
              hotLogicalMeteringFilter: {
                ...state.data.hotLogicalMeteringFilter,
                subfilter: null
              }
            }
          };
        }
        if (action.payload === MeteringType.ColdLogical) {
          return {
            ...state,
            data: {
              ...state.data,
              coldLogicalMeteringFilter: {
                ...state.data.coldLogicalMeteringFilter,
                subfilter: null
              }
            }
          };
        }
        return state;

    case ActionTypes.UserRadioSelection:
      switch (action.payload.meteringType) {
        case MeteringType.Hot:
          hotWidgets = state.hotWidgets;
          return {
            ...state,
            hotWidgets: hotWidgets.map(x => (x.name === action.payload.widget.name ? action.payload.widget : x))
          };
        case MeteringType.Cold:
          coldWidgets = state.coldWidgets;
          return {
            ...state,
            coldWidgets: coldWidgets.map(x => (x.name === action.payload.widget.name ? action.payload.widget : x))
          };
      }
      return state;
  }
  return state;
}

export const getSettings = (state: State) => state.data;
export const getLoaded = (state: State) => state.loaded;
export const getLoading = (state: State) => state.loading;
export const getAvailableWidgets = (state: State) => (state.data ? state.data.availableWidgets : []);
export const getHotWidgets = (state: State) => state.hotWidgets;
export const getColdWidgets = (state: State) => state.coldWidgets;
